import React, { useEffect, useState } from "react";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext, TabList } from "@mui/lab";
import { Form, Formik } from "formik";
import { Button, Grid, LinearProgress, Typography } from "@mui/material";
import useStyles from "../../../styles/styles";

import { validationSchema } from "./FormModel/validationSchema";
import { configState } from "../../../recoil/atoms";
import { useRecoilValue } from "recoil";
import {
  EditPatientDiagnosisRequest,
  GetPatientDiagnosisRendered,
  PatientDiagnosisApi,
} from "../../../api/medical-operations";
import DiagnosisForm from "./Forms/DiagnosisForm";
import PrescribedEquipments from "./Forms/PrescribedEquipments";
import PrescribedMedications from "./Forms/PrescribedMedications";
import {
  Diagnosis,
  DiagnosisApi,
  Equipment,
  EquipmentApi,
  Medication,
  MedicationApi,
} from "../../../api/medical-operations";
import { toast } from "react-toastify";

type Props = {
  initialPatientDiagnosisValues?: GetPatientDiagnosisRendered;
};
const EditPatientDiagnosis = ({ initialPatientDiagnosisValues }: Props) => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<string>("0");
  const config = useRecoilValue(configState);
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [patientDiagnosis, setPatientDiagnosis] = useState<GetPatientDiagnosisRendered>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const currentValidationSchema = validationSchema[parseInt(selectedTab)];

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };
  const getPatientDiagnosis = () => {
    const patientDiagnosis = new PatientDiagnosisApi(config);
    patientDiagnosis
      .getPatientDiagnosisById(initialPatientDiagnosisValues![0]!)
      .then((res) => {
        setIsLoading(false);
        setPatientDiagnosis(res?.data);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const editPatientDiagnosis = (values: EditPatientDiagnosisRequest) => {
    const PatientDiagnosis = new PatientDiagnosisApi(config);
    PatientDiagnosis.editPatientDiagnosis(initialPatientDiagnosisValues![0]!, values)
      .then((res) => {
            toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleSubmit = (values: any, actions: any) => {
    actions.setTouched({});
    actions.setSubmitting(false);
    const valuesEdited: EditPatientDiagnosisRequest = {
      Diagnoses: { DiagnosisID: values.Diagnoses?.DiagnosisID!, DoctorNotes: values.Diagnoses?.DoctorNotes },
      PrescribedEquipments: values?.PrescribedEquipments,
      PrescribedMedications: values?.PrescribedMedications,
      PrescriptionImage: values?.PrescriptionImage,
      ImageCategory: values?.ImageCategory
    };
    editPatientDiagnosis(valuesEdited);
  };

  const getDiagnoses = () => {
    const Diagnosis = new DiagnosisApi(config);
    Diagnosis.getDiagnoses()
      .then((res) => {
        setDiagnoses(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const getMedications = () => {
    const medication = new MedicationApi(config);
    medication
      .getMedication()
      .then((res) => {
        setMedications(res?.data);
      })
      .catch((err) => {
            toast.error(err?.response?.data?.detail);
      });
  };

  const getEquipment = () => {
    const Equipment = new EquipmentApi(config);
    Equipment.getEquipment()
      .then((res) => {
        setEquipment(res?.data);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getPatientDiagnosis();
    getEquipment();
    getMedications();
    getDiagnoses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ bgcolor: "#fff", p: 3 }}>
          {!isLoading ? (
            <>
              <Typography variant="h3" gutterBottom sx={{ p: 0 }}>
                Edit Patient Diagnosis
              </Typography>
              <TabContext value={selectedTab}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  width="100%"
                >
                  <TabList
                    onChange={handleChangeTab}
                    textColor="secondary"
                    indicatorColor="secondary"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                  >
                    <Tab label="Diagnosis" value="0" />
                    <Tab label="Prescribed Equipments" value="1" />
                    <Tab label="Prescribed Medications" value="2" />
                  </TabList>
                </Box>
                <Formik
                  initialValues={patientDiagnosis!}
                  enableReinitialize={true}
                  validationSchema={currentValidationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ dirty }) => {
                    return (
                      <Form>
                        <TabPanel value="0">
                          <DiagnosisForm diagnoses={diagnoses} />
                        </TabPanel>
                        <TabPanel value="1">
                          <PrescribedEquipments equipment={equipment} />
                        </TabPanel>
                        <TabPanel value="2">
                          <PrescribedMedications medications={medications} />
                        </TabPanel>
                        <Grid
                          container
                          sx={{
                            pt: 4,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Grid item>
                            <Button
                              type="submit"
                              variant="contained"
                              color="secondary"
                              className={classes.button}
                              fullWidth
                              disabled={!dirty}
                              onClick={() => handleSubmit}
                            >
                              Save
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>
                    );
                  }}
                </Formik>
              </TabContext>
            </>
          ) : (
            <LinearProgress color="secondary" />
          )}
        </Box>
      </Box>
    </>
  );
};

export default EditPatientDiagnosis;
